<script lang="ts">
	// Enhanced page component to handle special cases from server
	// @ts-nocheck
	import { onMount } from 'svelte';
	import Button from '$lib/components/ui/button/button.svelte';
	import { CalendarIcon, MapPin, Percent } from 'lucide-svelte';

	import * as Card from '$lib/components/ui/card';
	import * as Alert from '$lib/components/ui/alert';

	import Featured from '$lib/components/home/Featured.svelte';
	import TasIcon from '$lib/assets/icons/TasIcon.svelte';

	// Import will be needed in future implementation
	// import { browser } from '$app/environment';

	import { trip } from '$lib/utils/tripStore';
	import { toast } from 'svelte-sonner';
	import { enhancedImages } from '$lib/utils/images';
	import { trackNavigateToCheckout } from '$lib/utils/analytics';
	import { getRezdyCalendarLink, getRezdyBookingLink } from '$lib/data/rezdyBooking';
	import { selectTourDescriptionHtml } from '$lib/utils/selectTourDescriptionHtml';

	// Function to check if an image URL is in the enhancedImages collection
	function isEnhancedImage(imageUrl) {
		if (!imageUrl) return false;

		// Only consider local file paths as potential enhanced images
		// External URLs (containing http:// or https://) are not enhanced
		if (imageUrl.includes('http://') || imageUrl.includes('https://')) {
			return false;
		}

		// Try to find the image in our enhanced images collection
		try {
			return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		} catch (err) {
			console.warn('Error checking enhanced image:', err);
			return false;
		}
	}

	// Function to get the appropriate image source
	function getImageSource(imageUrl) {
		if (!imageUrl) return null;

		try {
			// If it's an enhanced image, get it from the enhancedImages collection
			const enhancedEntry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
			if (enhancedEntry && enhancedEntry[1] && enhancedEntry[1].default) {
				return enhancedEntry[1].default;
			}
		} catch (err) {
			console.warn('Error getting enhanced image source:', err);
		}

		// Otherwise, use the URL directly
		return imageUrl;
	}

	// Function to restore original image URLs after build
	function restoreOriginalImageUrls() {
		if (!tour || !tour.images) return;

		// Check each image for stored original URLs (from server-side)
		tour.images.forEach((img) => {
			if (img._originalUrl) {
				// Restore the original URL that was replaced during build
				img.itemUrl = img._originalUrl;
				delete img._originalUrl;
			}
		});
	}

	import { page } from '$app/stores';
	import { tourContent } from '$lib/utils/tourContent';
	import Gallery from '$lib/components/tours/Gallery.svelte';
	import DayTour from '$lib/components/tours/DayTour.svelte';
	import MultiDay from '$lib/components/tours/MultiDay.svelte';
	import TourReviews from '$lib/components/tours/TourReviews.svelte';
	// Availability component will be used in future updates
	import Seo from '$lib/components/SEO.svelte';

	// Dynamic image imports
	interface Props {
		data: import('./$types').PageData;
	}

	let { data }: Props = $props();

	// On component mount, restore original image URLs
	onMount(() => {
		restoreOriginalImageUrls();
		window.scrollTo(0, 0);
	});

	// Access the tour data directly from the 'data' prop
	const tour = data.tour;
	const structuredContent =
		tour.contentMode === 'rezdy-structured' && tour.parsedContent ? tour.parsedContent : null;
	const renderedLegacyDescription = selectTourDescriptionHtml(tour);
	// These will be used in future improvements when we implement the Availability component
	// let availabilityData = data.availabilityData;
	// let pickupData = data.pickupData?.pickupLocations || [];

	if (!tour) {
		throw new Error('Tour not found');
	}

	function toIsoDuration(minutes: number): string {
		if (minutes >= 1440) return `P${Math.round(minutes / 1440)}D`;
		const h = Math.floor(minutes / 60);
		const m = minutes % 60;
		return m === 0 ? `PT${h}H` : `PT${h}H${m}M`;
	}

	function getTourSchemaImages(images: any[], origin: string): string[] {
		return images
			.map((img) => {
				if (img.itemUrl && !img.itemUrl.startsWith('http')) {
					const entry = Object.entries(enhancedImages).find(([key]) => key === img.itemUrl);
					const src = (entry?.[1] as any)?.default?.img?.src;
					if (src) return `${origin}${src}`;
				}
				if (img.thumbnailUrl) return img.thumbnailUrl;
				if (img.itemUrl?.startsWith('http')) return img.itemUrl;
				return null;
			})
			.filter(Boolean)
			.slice(0, 5);
	}

	let touristTripSchema = $derived({
		'@context': 'https://schema.org',
		'@type': 'TouristTrip',
		name: tour.name,
		description: tour.shortDescription,
		image: getTourSchemaImages(tour.images ?? [], $page.url.origin),
		duration: toIsoDuration(tour.durationMinutes),
		offers: {
			'@type': 'Offer',
			price: tour.advertisedPrice.toFixed(2),
			priceCurrency: 'AUD',
			availability: 'https://schema.org/InStock',
			url: $page.url.href
		},
		provider: {
			'@type': 'TourOperator',
			name: 'Tassie Tours',
			url: $page.url.origin
		}
	});

	let tourBreadcrumbSchema = $derived({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: $page.url.origin },
			{ '@type': 'ListItem', position: 2, name: 'Tours', item: `${$page.url.origin}/tours` },
			{ '@type': 'ListItem', position: 3, name: tour.name, item: $page.url.href }
		]
	});

	let tourSchemaScript = $derived(
		`<script type="application/ld+json">${JSON.stringify(touristTripSchema)}${'<'}/script>` +
			`<script type="application/ld+json">${JSON.stringify(tourBreadcrumbSchema)}${'<'}/script>`
	);

	// Client-side refresh of availability data will be implemented in the future
	// let isLoadingData = false;

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);

		// Track Rezdy iframe interactions and get cleanup function
		const cleanupTracking = setupRezdyIframeTracking();

		// Refresh availability data on the client side - will be used in future implementation
		/*
		if (browser) {
			refreshAvailabilityData();
		}
		*/

		// Return cleanup function to be called when component unmounts
		return () => {
			if (cleanupTracking) {
				cleanupTracking();
			}
		};
	});

	/**
	 * Setup tracking for when user interacts with the Rezdy booking iframe
	 * Uses focus detection to identify when user clicks inside the iframe
	 */
	function setupRezdyIframeTracking() {
		let hasTrackedInteraction = false;
		let lastBlurTime = 0;
		const DEBOUNCE_DELAY = 1000; // 1 second debounce

		const trackIframeInteraction = () => {
			const currentTime = Date.now();

			// Check if active element is the Rezdy iframe and debounce repeated events
			const iframe = document.querySelector('iframe.rezdy');
			if (
				document.activeElement === iframe &&
				!hasTrackedInteraction &&
				currentTime - lastBlurTime > DEBOUNCE_DELAY
			) {
				hasTrackedInteraction = true;
				lastBlurTime = currentTime;

				// Track the event with tour data
				trackNavigateToCheckout({
					productCode: tour.productCode,
					tourName: tour.name,
					price: tour.advertisedPrice,
					productType: tour.productType
				});
			}
		};

		// Listen for blur events on window (indicates focus moved to iframe)
		window.addEventListener('blur', trackIframeInteraction);

		// Cleanup function
		return () => {
			window.removeEventListener('blur', trackIframeInteraction);
		};
	}

	// Function to refresh availability data from the API - will be used in future implementation
	/*
	async function refreshAvailabilityData() {
		if (!browser || isLoadingData) return;

		isLoadingData = true;
		try {
			// Get date range for availability
			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth() + 1;
			const startDate = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`;
			const endDate = new Date(currentYear + 1, currentMonth, 0).getDate();
			const endDateString = `${currentYear + 1}-${currentMonth.toString().padStart(2, '0')}-${endDate}`;

			// Get API key
			const apiKey = import.meta.env.VITE_REZDY_PROD;

			// Fetch latest availability data
			const availabilityUrl = `https://api.rezdy.com/v1/availability?apiKey=${apiKey}&productCode=${tour.productCode}&startTimeLocal=${startDate} 00:00:00&endTimeLocal=${endDateString} 00:00:00`;
			const pickupUrl = `https://api.rezdy.com/v1/products/${tour.productCode}/pickups?apiKey=${apiKey}`;

			const [availabilityResponse, pickupResponse] = await Promise.all([
				fetch(availabilityUrl),
				fetch(pickupUrl)
			]);

			if (availabilityResponse.ok) {
				availabilityData = await availabilityResponse.json();
			}

			if (pickupResponse.ok) {
				const pickupResult = await pickupResponse.json();
				if (pickupResult && pickupResult.pickupLocations) {
					pickupData = pickupResult.pickupLocations;
				}
			}
		} catch (err) {
			console.error('Error refreshing availability data:', err);
		} finally {
			isLoadingData = false;
		}
	}
	*/

	function formatDuration(durationMinutes) {
		const hours = Math.ceil(durationMinutes / 60); // Round up to nearest hour
		if (hours >= 24) {
			const days = Math.ceil(hours / 24); // Round up to nearest day
			return `${days} day${days > 1 ? 's' : ''}`;
		} else {
			return `${hours} hour${hours > 1 ? 's' : ''}`;
		}
	}

	function formatPrice(price) {
		return `$${price.toFixed(2)}`;
	}

	// Function moved to a utility file to be imported when needed

	function formatLocation(locationAddress) {
		if (locationAddress && locationAddress.city && locationAddress.state) {
			return `${locationAddress.city}, ${locationAddress.state}`;
		} else {
			return 'Location unavailable'; // Or any other suitable default message
		}
	}
	function addToTrip() {
		trip.update((currentTrips) => {
			const isAlreadyInTrip = currentTrips.some((t) => t.productCode === tour.productCode);

			if (isAlreadyInTrip) {
				toast.error(`${tour.name} is already in your trip`);
				return currentTrips;
			}

			toast.success(`${tour.name} successfully added to trip`);
			return [...currentTrips, tour];
		});
	}

	function handleAnchorClick(event) {
		event.preventDefault();
		const link = event.currentTarget;
		const anchorId = new URL(link.href).hash.replace('#', '');
		const anchor = document.getElementById(anchorId);
		window.scrollTo({
			top: anchor.offsetTop,
			behavior: 'smooth'
		});
	}
</script>

<svelte:head>
	{@html tourSchemaScript}
</svelte:head>

<Seo
	title="{tour.name} - Tassie Tours"
	description={tour.shortDescription}
	keywords="Tasmania Tours, Tasmania local tour, Tassie Tours, Day tours from Hobart, Discover Bruny Island, Cradle Mountain, Active Tasmania tours"
	siteName="Tassie Tours"
	imageURL={tour.images[0].itemUrl}
	logo="/logo/logo-black.png"
	author="Tassie Tours"
	type="Tour"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
	socials={[]}
/>

<!-- HERO -->
<section class="relative mx-auto flex h-[600px] flex-col items-center justify-center">
	{#if tour.images && tour.images.length > 0}
		{#if tour.images[0] && isEnhancedImage(tour.images[0].itemUrl)}
			<enhanced:img
				src={getImageSource(tour.images[0].itemUrl)}
				alt={tour.name}
				class="absolute inset-0 h-[600px] w-full object-cover"
				sizes="(min-width: 1280px) 1280px, 100vw"
				fetchpriority="high"
			/>
		{:else}
			<img
				src={tour.images[0].itemUrl}
				alt={tour.name || 'Tour Image'}
				loading="eager"
				class="absolute inset-0 h-full w-full object-cover"
				onerror={(e) => {
					console.warn('Image failed to load:', tour.images[0].itemUrl);
					e.currentTarget.onerror = null;
					e.currentTarget.src = '/images/placeholder-tour.jpg';
				}}
			/>
		{/if}
	{:else}
		<div class="absolute inset-0 h-[600px] w-full bg-muted"></div>
	{/if}

	<div class="absolute inset-0 bg-foreground/20"></div>

	<div
		class="pointer-events-none absolute inset-0 mx-auto flex h-[600px] w-full max-w-7xl flex-col justify-center px-4"
	>
		<h1
			class="mx-auto max-w-2xl scroll-m-20 text-center font-heading text-2xl font-bold tracking-tight text-background lg:mx-0 lg:text-left lg:text-4xl"
		>
			{tour.name}
		</h1>
		<div class="mx-auto mt-6 flex items-center lg:mx-0">
			<p class=" flex flex-col font-heading text-xl font-bold text-background lg:text-3xl">
				<span class="font-sans text-xs">From AUD </span>
				{tour.advertisedPrice > 1000
					? `$${tour.advertisedPrice.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1 ')}`
					: formatPrice(tour.advertisedPrice)}
			</p>
		</div>
		<div class="mx-auto flex gap-4 pt-12 font-medium text-background lg:mx-0">
			<div class="flex items-center">
				<CalendarIcon class="mr-2 h-6 w-6 stroke-background/80 " />
				<p class="lg:text-md text-sm">{formatDuration(tour.durationMinutes)}</p>
			</div>
			<div class="flex items-center">
				<MapPin class="mr-2 h-6 w-6 stroke-background/80 " />
				<p class="lg:text-md text-sm">{formatLocation(tour.locationAddress)}</p>
			</div>
		</div>
		<div class="mx-auto flex flex-col gap-4 md:flex-row lg:mx-0">
			<Button
				data-sveltekit-noscroll
				href="#book"
				onclick={handleAnchorClick}
				class="pointer-events-auto mx-auto mt-8 w-44 rounded-full bg-tt-red px-6 text-lg font-semibold hover:bg-tt-red/90 lg:mx-0"
				>Book now</Button
			>
			<Button
				variant="outline"
				onclick={addToTrip}
				data-sveltekit-noscroll
				class="pointer-events-auto mx-auto w-44 rounded-full border-2 bg-transparent px-6 text-lg font-semibold text-background md:mt-8  lg:mx-0"
				>Add to trip</Button
			>
		</div>
	</div>
</section>

<!-- GALLERY -->
<Gallery {tour} />

<!-- CONTENT & BOOKING -->
<section id="book" class="mx-auto flex max-w-7xl flex-col px-4 pb-12 lg:pb-20 lg:pt-20">
	<div class="relative flex w-full flex-col lg:grid lg:grid-cols-4 lg:gap-4">
		{#if structuredContent}
			<div class="col-span-3">
				<h2
					class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
				>
					Overview
				</h2>
				<div class="prose mt-6 max-w-none" id="tourDescription">
					<!-- eslint-disable-next-line svelte/no-at-html-tags -- parsed Rezdy HTML is allowlist-sanitized -->
					{@html structuredContent.overviewHtml}
				</div>

				{#if structuredContent.importantInformationHtml || structuredContent.unknownSections.length > 0}
					<div class="mb-10 mt-8 rounded-2xl bg-muted p-6 lg:mb-0 lg:mt-10 lg:p-8">
						<h3 class="font-heading text-xl font-semibold tracking-tight">Important information</h3>
						<div class="prose mt-4 max-w-none">
							<!-- eslint-disable-next-line svelte/no-at-html-tags -- parsed Rezdy HTML is allowlist-sanitized -->
							{@html structuredContent.importantInformationHtml}
						</div>
						{#each structuredContent.unknownSections as section}
							<h3 class="mt-8 font-heading text-xl font-semibold tracking-tight">
								{section.title}
							</h3>
							<!-- eslint-disable-next-line svelte/no-at-html-tags -- parsed Rezdy HTML is allowlist-sanitized -->
							<div class="prose mt-4 max-w-none">{@html section.html}</div>
						{/each}
					</div>
				{/if}
			</div>
		{:else if tourContent.some((t) => t.productCode === tour.productCode)}
			<div class="col-span-3">
				<h2
					class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
				>
					{tourContent.find((t) => t.productCode === tour.productCode)?.headline}
				</h2>
				<div class="prose mt-6 max-w-none" id="tourDescription">
					<!-- {@html tourContent.find((t) => t.productCode === tour.productCode)?.description} -->
					<!-- eslint-disable-next-line svelte/no-at-html-tags -- legacy unstructured path retained during the pilot -->
					{@html renderedLegacyDescription}
				</div>
			</div>
		{:else}
			<div class="col-span-3">
				<h2
					class="mt-10 scroll-m-20 pb-2 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
				>
					About this tour
				</h2>
				<div class="prose mt-6 max-w-none" id="tourDescription">
					<!-- eslint-disable-next-line svelte/no-at-html-tags -- legacy unstructured path retained for non-pilot products -->
					{@html renderedLegacyDescription}
				</div>
			</div>
		{/if}

		<!-- <Availability {tour} {availabilityData} {pickupData} /> -->

		<!-- TEMPORARY SOLUTIONS -->
		<div id="book" class="col-span-1 h-fit self-start lg:sticky lg:top-16">
			<Card.Root class="mx-auto rounded-2xl border-none bg-[#f4f5f7] shadow-none">
				<Card.Header>
					<Card.Title class="font-heading text-xl">Book now</Card.Title>
					<Card.Description>Enter your information below to secure your booking</Card.Description>
				</Card.Header>
				<Card.Content class="mx-0 mt-0 px-0 pt-0">
					<!-- <div class="grid gap-4">
						<Button
							target="_blank"
							href={getRezdyBookingLink(tour.productCode, tour.slug)}
							class="button-booking rezdy rezdy-modal w-full rounded-full bg-tt-red hover:bg-tt-red/80"
						>
							Book Now
						</Button>

						<Button onclick={addToTrip} variant="outline" class="w-full rounded-full"
							>Add to trip</Button
						>
					</div>
					<div class="mt-4 text-center text-sm">
						<span class="underline">Please note:</span> After your purchase is confirmed we will email
						you a confirmation.
					</div> -->
					<iframe
						seamless=""
						width="100%"
						height="500px"
						frameborder="0"
						class="rezdy"
						title="Rezdy Booking Modal"
						src={getRezdyCalendarLink(tour.productCode)}
					></iframe>
				</Card.Content>
			</Card.Root>
			{#if tour.productType === 'DAYTOUR'}
				<a href="/tours#dayTours" data-sveltekit-noscroll data-sveltekit-prefetch>
					<Alert.Root class="relative mx-auto mt-8 w-full rounded-xl bg-tt-red text-white">
						<Alert.Description class="text-md px-2 text-center">
							<span class="">Book 2+ day tours</span> and receive
							<span class="font-black">15% OFF</span> your order
						</Alert.Description>
						<div class="absolute -left-3 -top-3 m-0 flex h-12 w-12 items-center justify-center p-0">
							<div class="relative flex h-full w-full items-center justify-center">
								<TasIcon class="shadow-lg" />
								<div class="absolute inset-0 flex items-center justify-center">
									<Percent class="h-5 w-5 stroke-foreground" />
								</div>
							</div>
						</div>
					</Alert.Root>
				</a>
			{/if}
		</div>
	</div>
</section>

{#if tour.productType === 'DAYTOUR'}
	<DayTour {tour} />
{:else if tour.productType === 'MULTIDAYTOUR'}
	<MultiDay {tour} rezdyLink={getRezdyBookingLink(tour.productCode, tour.slug)} />
{/if}

<TourReviews {tour} />

{#if tour.productType === 'DAYTOUR'}
	<Featured
		heading="Click or swipe to explore more day tours"
		selected="dayTours"
		productCode={tour.productCode}
	/>
{:else if tour.productType === 'MULTIDAYTOUR'}
	<Featured
		heading="Click or swipe to explore more multi-day tours"
		selected="multiDayTours"
		productCode={tour.productCode}
	/>
{:else if tour.productType === 'SHOREEXCURSION'}
	<Featured
		heading="Click or swipe to explore more shore excursions"
		selected="shoreExcursions"
		productCode={tour.productCode}
	/>
{:else if tour.productType === 'PRIVATETOUR'}
	<Featured
		heading="Click or swipe to explore more private tours"
		selected="privateTours"
		productCode={tour.productCode}
	/>
{/if}
